import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { scaleLinear } from 'd3-scale';
import { line } from 'd3-shape';
import {
  CountableTimeInterval,
  timeDay,
  timeMonth,
  timeWeek,
  timeYear,
} from 'd3-time';
import { sortBy } from 'lodash';
import * as moment from 'moment';
import { ObservableInput } from 'observable-input';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { Size } from '../core/size.service';
import { DatedDataPoint } from './data';

type TimeSeriesDatum = { key: number; value: number };

enum Interval {
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month',
  YEAR = 'year',
}

const intervalMap = new Map<Interval, CountableTimeInterval>([
  [Interval.DAY, timeDay],
  [Interval.WEEK, timeWeek],
  [Interval.MONTH, timeMonth],
  [Interval.YEAR, timeYear],
]);

type XTick = {
  value: number;
  label: string;
};

type Marker = {
  x: number;
  y: number;
  label: string;
};

const TICK_LIMIT_THRESHOLD = 15;

@Component({
  selector: 'prepared-time-series',
  templateUrl: './prepared-time-series.component.html',
  styleUrls: ['./prepared-time-series.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PreparedTimeSeriesComponent<T> {
  @ObservableInput()
  @Input()
  data!: Observable<DatedDataPoint<T>[] | null>;

  @ObservableInput()
  @Input()
  minDate!: Observable<moment.Moment>;

  @ObservableInput()
  @Input()
  maxDate!: Observable<moment.Moment>;

  @ObservableInput()
  @Input()
  getMetric!: Observable<(metrics: T) => number>;

  @ObservableInput()
  @Input()
  valueFormatter!: Observable<(value: number) => string>;

  readonly interval: Observable<Interval> = combineLatest([
    this.minDate,
    this.maxDate,
  ]).pipe(
    map(([min, max]) => {
      const diff = max.diff(min, 'days');
      if (diff >= 730) {
        return Interval.YEAR;
      } else if (diff >= 365) {
        return Interval.MONTH;
      } else if (diff >= 90) {
        return Interval.WEEK;
      } else {
        return Interval.DAY;
      }
    }),
  );

  readonly timeSeriesData: Observable<TimeSeriesDatum[]> = combineLatest([
    this.data,
    this.getMetric,
  ]).pipe(
    filter(([data]) => data !== null),
    map(([data, getMetric]) =>
      data!.map(datum => ({
        key: datum.date.valueOf(),
        value: getMetric(datum.metricData),
      })),
    ),
    map(data => sortBy(data, 'key')),
  );

  readonly empty = this.timeSeriesData.pipe(map(data => data.length === 0));

  readonly svgSize = new Subject<Size>();

  readonly yMax: Observable<number> = this.timeSeriesData.pipe(
    map(data =>
      Math.max.apply(
        null,
        data.map(({ value }) => value),
      ),
    ),
    map(max => max * 1.05),
  );

  readonly plotBottomOffset = this.data.pipe(
    filter(data => data !== null),
    map(data => (data!.length > 1 ? 56 : 24)),
  );

  readonly plotLeftOffset = of(48);

  readonly yScale = combineLatest([
    this.svgSize,
    this.plotBottomOffset,
    this.yMax,
  ]).pipe(
    map(([size, plotBottomOffset, yMax]) =>
      scaleLinear()
        .domain([0, yMax])
        .range([size.height - plotBottomOffset, 0]),
    ),
  );

  readonly yTicks: Observable<number[]> = this.yScale.pipe(
    map(scale => scale.ticks(5)),
  );

  readonly xScale = combineLatest([
    this.minDate,
    this.maxDate,
    this.svgSize,
    this.plotLeftOffset,
  ]).pipe(
    map(([minDate, maxDate, size, offset]) =>
      scaleLinear()
        .domain([minDate.valueOf(), maxDate.valueOf()])
        .range([offset, size.width]),
    ),
  );

  readonly minorIntervalFormat = this.interval.pipe(
    map(interval => {
      switch (interval) {
        case Interval.DAY:
        case Interval.WEEK:
          return 'D';
        case Interval.MONTH:
          return 'MMM';
        case Interval.YEAR:
          return "'YY";
      }
    }),
  );

  readonly xMinorTicks: Observable<XTick[]> = combineLatest([
    this.minDate,
    this.maxDate,
    this.interval,
    this.minorIntervalFormat,
  ]).pipe(
    map(([minDate, maxDate, interval, format]) =>
      intervalMap
        .get(interval)!
        .range(minDate.toDate(), maxDate.toDate())
        .map(date => ({
          value: date.valueOf(),
          label: moment(date).format(format),
        }))
        .filter((_, i, arr) =>
          arr.length > TICK_LIMIT_THRESHOLD
            ? i % Math.round(arr.length / TICK_LIMIT_THRESHOLD) === 0
            : true,
        ),
    ),
  );

  readonly majorInterval = this.interval.pipe(
    map(interval => {
      switch (interval) {
        case Interval.DAY:
        case Interval.WEEK:
          return Interval.MONTH;
        default:
          return Interval.YEAR;
      }
    }),
  );

  readonly majorIntervalFormat = this.majorInterval.pipe(
    map(interval => {
      switch (interval) {
        case Interval.YEAR:
          return 'YYYY';
        case Interval.MONTH:
          return 'MMM YYYY';
        default:
          return 'DD';
      }
    }),
  );

  readonly xMajorTicks: Observable<XTick[]> = combineLatest([
    this.majorInterval,
    this.majorIntervalFormat,
    this.minDate,
    this.maxDate,
  ]).pipe(
    map(([interval, format, minDate, maxDate]) => {
      const date = minDate.clone().startOf(interval);
      const values = [];
      while (date.isBefore(maxDate)) {
        if (date.isAfter(minDate)) {
          values.push({ label: date.format(format), value: date.valueOf() });
        }
        date.add(1, interval);
      }
      return values;
    }),
  );

  readonly seriesPath: Observable<string> = combineLatest([
    this.xScale,
    this.yScale,
    this.timeSeriesData,
  ]).pipe(
    map(
      ([xScale, yScale, timeSeriesData]) =>
        line<TimeSeriesDatum>()
          .x(d => xScale(d.key)!)
          .y(d => yScale(d.value)!)(timeSeriesData)!,
    ),
  );

  readonly seriesMarkers: Observable<Marker[]> = combineLatest([
    this.xScale,
    this.yScale,
    this.timeSeriesData,
    this.valueFormatter,
  ]).pipe(
    map(([xScale, yScale, timeSeriesData, formatter]) =>
      timeSeriesData
        .filter(item => Boolean(item.value))
        .map(item => ({
          x: xScale(item.key)!,
          y: yScale(item.value)!,
          label: formatter(item.value),
        })),
    ),
  );
}
